import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import api from '../../services/api';
import { buildWaMeUrl } from '../../utils/whatsappSupport';
import PhoneField from '../ui/PhoneField';

const TABS = [
  { id: 'templates', label: 'Plantillas' },
  { id: 'whatsapp', label: 'WhatsApp planes' },
  { id: 'email', label: 'Email' },
];

const EVENT_OPTIONS = [
  'user.registered',
  'payment.approved',
  'payment.rejected',
  'd28d.class.scheduled',
  'd28d.class.time_changed',
  'cycle.started',
  'license.expiring',
  'license.expired',
  'license.reactivated',
  'training.assigned',
  'support.whatsapp.click',
];

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'in_app', label: 'In-app' },
  { value: 'whatsapp_link', label: 'WhatsApp (wa.me auditoría)' },
  { value: 'whatsapp_api', label: 'WhatsApp API (WABA)' },
];

const EMPTY_FORM = {
  nombre: '',
  evento: 'user.registered',
  modulo: 'd28d',
  canal: 'email',
  asunto: '',
  contenido: '',
  activo: true,
};

export default function CommunicationCenterAdmin({ onBack }) {
  const [tab, setTab] = useState('templates');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allowedModules, setAllowedModules] = useState(['d28d', 'food', 'training', 'platform']);
  const [loading, setLoading] = useState(true);
  const [editingTpl, setEditingTpl] = useState(null);
  const [tplForm, setTplForm] = useState(EMPTY_FORM);

  const [editingPlan, setEditingPlan] = useState(null);
  const [waForm, setWaForm] = useState({
    support_whatsapp: '573192635819',
    support_name: 'Soporte D28D',
    support_message: '',
    support_activo: true,
  });

  const [emailTest, setEmailTest] = useState({ to: '', evento: 'user.registered', modulo: 'd28d' });
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [mailStatus, setMailStatus] = useState(null);

  const moduleOptions = useMemo(
    () => (allowedModules.length ? allowedModules : ['d28d', 'food', 'training', 'platform']),
    [allowedModules],
  );

  const loadPlans = useCallback(async () => {
    try {
      const res = await api.get('/accounts/plans', { params: { platform_only: 'true' } });
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPlans([]);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    const res = await api.get('/communications/templates');
    setTemplates(Array.isArray(res.data?.data) ? res.data.data : []);
    if (Array.isArray(res.data?.meta?.allowed_modules)) {
      setAllowedModules(res.data.meta.allowed_modules);
    }
  }, []);

  const loadMailStatus = useCallback(async () => {
    try {
      const res = await api.get('/communications/mail-status');
      setMailStatus(res.data?.data || null);
    } catch {
      setMailStatus(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadTemplates(), loadPlans(), loadMailStatus()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [loadTemplates, loadPlans, loadMailStatus]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startNewTemplate = () => {
    setEditingTpl({ id: null });
    setTplForm({ ...EMPTY_FORM, modulo: moduleOptions[0] || 'd28d' });
    setMessage('');
    setError('');
  };

  const startEditTemplate = (tpl) => {
    setEditingTpl(tpl);
    setTplForm({
      nombre: tpl.nombre || '',
      evento: tpl.evento || 'user.registered',
      modulo: tpl.modulo || 'd28d',
      canal: tpl.canal || 'email',
      asunto: tpl.asunto || '',
      contenido: tpl.contenido || '',
      activo: tpl.activo !== false,
    });
    setMessage('');
    setError('');
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingTpl?.id) {
        await api.put(`/communications/templates/${editingTpl.id}`, tplForm);
        setMessage('Plantilla actualizada');
      } else {
        await api.post('/communications/templates', tplForm);
        setMessage('Plantilla creada');
      }
      setEditingTpl(null);
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Error guardando plantilla');
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`¿Eliminar plantilla «${tpl.nombre}»?`)) return;
    try {
      await api.delete(`/communications/templates/${tpl.id}`);
      setMessage('Plantilla eliminada');
      if (editingTpl?.id === tpl.id) setEditingTpl(null);
      await loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Error eliminando plantilla');
    }
  };

  const startEditPlanWa = (plan) => {
    setEditingPlan(plan);
    setWaForm({
      support_whatsapp: plan.support_whatsapp || '573192635819',
      support_name: plan.support_name || 'Soporte D28D',
      support_message: plan.support_message || '',
      support_activo: plan.support_activo !== false,
    });
  };

  const savePlanWa = async (e) => {
    e.preventDefault();
    if (!editingPlan) return;
    try {
      await api.put(`/accounts/plans/${encodeURIComponent(editingPlan.nombre)}`, waForm);
      setMessage('WhatsApp del plan actualizado');
      setEditingPlan(null);
      await loadPlans();
    } catch (err) {
      setError(err.response?.data?.error || 'Error guardando WhatsApp');
    }
  };

  const previewUrl = buildWaMeUrl(waForm.support_whatsapp, waForm.support_message);

  const sendTestEmail = async (e) => {
    e.preventDefault();
    setError('');
    setEmailTestResult(null);
    try {
      const res = await api.post('/communications/email/test', emailTest);
      setEmailTestResult(res.data?.data || null);
      setMessage('Email de prueba enviado correctamente');
    } catch (err) {
      const payload = err.response?.data || null;
      setEmailTestResult(payload?.data || payload || null);
      const detail = payload?.error || payload?.message || err.message;
      setError(detail || 'Error enviando email de prueba. Verifica MAIL_PROVIDER y credenciales SMTP/SendGrid en el backend.');
    }
  };

  const scopeLabel = (tpl) => {
    if (tpl.trainerId || tpl.trainer_id) return `Coach #${tpl.trainerId || tpl.trainer_id}`;
    if (tpl.gymId || tpl.gym_id) return `Gym #${tpl.gymId || tpl.gym_id}`;
    return 'Plataforma';
  };

  return (
    <div className="dashboard-main-view space-y-6">
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button type="button" className="btn-secondary" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Volver
            </button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <MessageCircle className="w-7 h-7 text-lime-600" />
              Centro de Comunicación
            </h2>
            <p className="text-sm text-stone-600">
              Plantillas por módulo (D28D / Food / Training), WhatsApp y prueba de email. WABA se configura en Mi Marca / Marca del coach.
            </p>
          </div>
        </div>
        <button type="button" className="btn-secondary" onClick={refresh}>Actualizar</button>
      </header>

      {error && <div className="error-message">{error}</div>}
      {message && <div className="bg-lime-50 border border-lime-200 text-lime-800 p-3 rounded-xl text-sm">{message}</div>}

      <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === t.id ? 'bg-lime-600 text-white' : 'bg-stone-100 text-stone-700'
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-stone-500">Cargando…</p>
      ) : (
        <>
          {tab === 'templates' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="card space-y-2 max-h-[32rem] overflow-y-auto">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-semibold">Plantillas</h4>
                  <button type="button" className="btn-primary text-sm" onClick={startNewTemplate}>Nueva</button>
                </div>
                {templates.length === 0 ? (
                  <p className="text-sm text-stone-500">No hay plantillas en tu alcance.</p>
                ) : templates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className={`p-3 rounded-lg border ${
                      editingTpl?.id === tpl.id ? 'border-lime-500 bg-lime-50' : 'border-stone-200'
                    }`}
                  >
                    <button type="button" className="w-full text-left" onClick={() => startEditTemplate(tpl)}>
                      <p className="font-medium text-sm text-stone-900">{tpl.nombre}</p>
                      <p className="text-xs text-stone-500">
                        {tpl.evento} · {tpl.modulo} · {tpl.canal} · {scopeLabel(tpl)}
                      </p>
                    </button>
                    <div className="mt-2 flex gap-2">
                      <button type="button" className="btn-secondary text-xs" onClick={() => startEditTemplate(tpl)}>Editar</button>
                      <button type="button" className="btn-secondary text-xs text-red-700" onClick={() => deleteTemplate(tpl)}>Eliminar</button>
                    </div>
                  </div>
                ))}
              </div>

              {editingTpl && (
                <form onSubmit={saveTemplate} className="card space-y-3">
                  <h4 className="font-semibold">{editingTpl.id ? 'Editar plantilla' : 'Nueva plantilla'}</h4>
                  <input
                    className="input w-full"
                    placeholder="Nombre"
                    value={tplForm.nombre}
                    onChange={(e) => setTplForm((p) => ({ ...p, nombre: e.target.value }))}
                    required
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="input"
                      value={tplForm.evento}
                      onChange={(e) => setTplForm((p) => ({ ...p, evento: e.target.value }))}
                    >
                      {EVENT_OPTIONS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                    </select>
                    <select
                      className="input"
                      value={tplForm.modulo}
                      onChange={(e) => setTplForm((p) => ({ ...p, modulo: e.target.value }))}
                    >
                      {moduleOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <select
                    className="input w-full"
                    value={tplForm.canal}
                    onChange={(e) => setTplForm((p) => ({ ...p, canal: e.target.value }))}
                  >
                    {CHANNEL_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <input
                    className="input w-full"
                    placeholder="Asunto (email / in-app)"
                    value={tplForm.asunto}
                    onChange={(e) => setTplForm((p) => ({ ...p, asunto: e.target.value }))}
                  />
                  <textarea
                    className="input w-full"
                    rows={8}
                    placeholder="Contenido. Variables: {{user.nombre}}, {{access.url}}, …"
                    value={tplForm.contenido}
                    onChange={(e) => setTplForm((p) => ({ ...p, contenido: e.target.value }))}
                    required
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={tplForm.activo}
                      onChange={(e) => setTplForm((p) => ({ ...p, activo: e.target.checked }))}
                    />
                    Activa
                  </label>
                  <div className="flex gap-2">
                    <button type="submit" className="btn-primary flex-1">Guardar</button>
                    <button type="button" className="btn-secondary" onClick={() => setEditingTpl(null)}>Cancelar</button>
                  </div>
                </form>
              )}
            </div>
          )}

          {tab === 'whatsapp' && (
            <div className="space-y-4">
              <p className="text-sm text-stone-600">
                Número global por defecto: <strong>+57 319 263 5819</strong> (wa.me).
                API Meta (WABA) se configura en <strong>Mi Marca</strong> (gym/sede) o <strong>Marca del coach</strong>.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="card space-y-2 max-h-96 overflow-y-auto">
                  <h4 className="font-semibold">Planes</h4>
                  {plans.map((p) => (
                    <button
                      key={p.nombre}
                      type="button"
                      className={`w-full text-left p-3 rounded-lg border ${
                        editingPlan?.nombre === p.nombre ? 'border-lime-500 bg-lime-50' : 'border-stone-200'
                      }`}
                      onClick={() => startEditPlanWa(p)}
                    >
                      <p className="font-medium text-sm">{p.nombre}</p>
                      <p className="text-xs text-stone-500">{p.kind} · {p.program_id}</p>
                    </button>
                  ))}
                </div>
                {editingPlan && (
                  <form onSubmit={savePlanWa} className="card space-y-3">
                    <h4 className="font-semibold">Editar: {editingPlan.nombre}</h4>
                    <PhoneField
                      label="WhatsApp soporte"
                      value={waForm.support_whatsapp}
                      onChange={(v) => setWaForm({ ...waForm, support_whatsapp: v })}
                      inputClassName="input"
                      placeholder="319 263 5819"
                    />
                    <input className="input" placeholder="Nombre soporte" value={waForm.support_name}
                      onChange={(e) => setWaForm({ ...waForm, support_name: e.target.value })} />
                    <textarea className="input" rows={3} placeholder="Mensaje precargado" value={waForm.support_message}
                      onChange={(e) => setWaForm({ ...waForm, support_message: e.target.value })} />
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={waForm.support_activo}
                        onChange={(e) => setWaForm({ ...waForm, support_activo: e.target.checked })} />
                      Activo
                    </label>
                    <a href={previewUrl} target="_blank" rel="noreferrer" className="text-lime-700 text-sm underline">
                      Probar enlace wa.me
                    </a>
                    <button type="submit" className="btn-primary w-full">Guardar plan</button>
                  </form>
                )}
              </div>
            </div>
          )}

          {tab === 'email' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900">Configuración Email (Shell)</h3>
              <p className="text-sm text-stone-600">
                SMTP del ecosistema (igual para D28D, Food y Training vía Communication Center).
                Food Nest puede seguir con sus propios correos internos; esta pantalla valida el envío del shell.
              </p>

              {mailStatus ? (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm space-y-1">
                  <p><strong>Proveedor:</strong> {mailStatus.provider || '—'}</p>
                  <p><strong>Remitente (MAIL_FROM):</strong> {mailStatus.from || '—'}</p>
                  <p><strong>Estado:</strong> {mailStatus.configured ? 'Configurado' : 'Faltan variables de entorno'}</p>
                  {!mailStatus.configured && mailStatus.missing?.length ? (
                    <p className="text-amber-800 text-xs">Variables pendientes: {mailStatus.missing.join(', ')}</p>
                  ) : null}
                </div>
              ) : null}

              <form onSubmit={sendTestEmail} className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-stone-600 font-medium">Destino (to)</label>
                    <input
                      className="input w-full"
                      value={emailTest.to}
                      onChange={(e) => setEmailTest((p) => ({ ...p, to: e.target.value }))}
                      placeholder="correo@dominio.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-600 font-medium">Evento</label>
                    <select
                      className="input w-full"
                      value={emailTest.evento}
                      onChange={(e) => setEmailTest((p) => ({ ...p, evento: e.target.value }))}
                    >
                      {EVENT_OPTIONS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-stone-600 font-medium">Módulo</label>
                    <select
                      className="input w-full"
                      value={emailTest.modulo}
                      onChange={(e) => setEmailTest((p) => ({ ...p, modulo: e.target.value }))}
                    >
                      {moduleOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>

                <button type="submit" className="btn-primary">Enviar email de prueba</button>
              </form>

              {emailTestResult && (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm text-stone-800">
                  <div className="font-semibold text-stone-900 mb-1">Resultado</div>
                  <pre className="mt-3 p-3 bg-stone-900 text-stone-100 rounded-lg text-[11px] overflow-auto whitespace-pre-wrap">
                    {JSON.stringify(emailTestResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
